import { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  Button,
  Paper,
  Divider,
  Fade,
  Zoom,
  alpha,
  useTheme,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassEmptyIcon,
  People as PeopleIcon,
  EmojiEvents as TrophyIcon,
  Bolt as BoltIcon,
  RocketLaunch as RocketIcon,
  Psychology as PsychologyIcon,
  Lightbulb as LightbulbIcon,
  BarChart as BarChartIcon,
  ChevronRight as ChevronRightIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { TABLES, RELATIONS, getCurrentSchoolYear, getSchoolYears, resolveSchoolYear } from '../lib/tables';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import StudentStats from '../components/StudentStats';
import CategoryCredits from '../components/CategoryCredits';
import SchoolYearSelect from '../components/SchoolYearSelect';
import SchoolYearBadge from '../components/SchoolYearBadge';
import Alert from '@mui/material/Alert';

export default function Dashboard() {
  const navigate = useNavigate();
  const { profile, loading: authLoading } = useAuth();
  const theme = useTheme();
  
  const [loading, setLoading] = useState(true);
  const [guideDialogOpen, setGuideDialogOpen] = useState(false);
  const [stats, setStats] = useState({
    totalCredits: 0,
    pendingRequests: 0,
    validatedRequests: 0,
    completedModules: 0,
    totalStudents: 0,
    averageCredits: 0,
    requestsToReview: 0,
  });
  
  const [recentRequests, setRecentRequests] = useState([]);
  const [quickActions, setQuickActions] = useState([]);
  const [isMounted, setIsMounted] = useState(true);

  const [selectedYear, setSelectedYear] = useState(null);
  const [yearOptions, setYearOptions] = useState([]);

  useEffect(() => {
    if (!profile || profile.rol !== 'elev') return undefined;
    let mounted = true;
    getSchoolYears(TABLES.credit_requests).then((years) => {
      if (!mounted) return;
      setYearOptions(years);
      setSelectedYear((prev) => prev ?? resolveSchoolYear(years));
    });
    return () => { mounted = false; };
  }, [profile]);

  useEffect(() => {
    setIsMounted(true);
    if (profile && (profile.rol !== 'elev' || selectedYear)) {
      loadDashboardData();
    }
    return () => setIsMounted(false);
  }, [profile, selectedYear]);

  const loadDashboardData = async () => {
    if (!isMounted) return;
    setLoading(true);
    try {
      switch (profile.rol) {
        case 'elev':
          await loadStudentDashboard();
          break;
        case 'profesor':
          await loadTeacherDashboard();
          break;
        case 'coordonator':
          await loadCoordinatorDashboard();
          break;
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      if (isMounted) setLoading(false);
    }
  };

  const loadStudentDashboard = async () => {
    const yearFilter = selectedYear === 'all' ? yearOptions : [selectedYear || getCurrentSchoolYear()];
    
    
    const { data: requestsData } = await supabase
      .from(TABLES.credit_requests)
      .select(`
        *,
        ${RELATIONS.credit_types}(categorie)
      `)
      .eq('id_elev', profile.id)
      .in('an_scolar', yearFilter)
      .order('data_cerere', { ascending: false });

    
    const validatedRequests = requestsData?.filter(r => r.status === 'validat') || [];
    const creditsByCategory = {
      curricular: validatedRequests
        .filter(r => (r[RELATIONS.credit_types] || r.credit_types_v2 || r.credit_types)?.categorie === 'curricular')
        .reduce((sum, r) => sum + (r.credite_acordate || 0), 0),
      extracurricular: validatedRequests
        .filter(r => (r[RELATIONS.credit_types] || r.credit_types_v2 || r.credit_types)?.categorie === 'extracurricular')
        .reduce((sum, r) => sum + (r.credite_acordate || 0), 0),
      transferabil: validatedRequests
        .filter(r => (r[RELATIONS.credit_types] || r.credit_types_v2 || r.credit_types)?.categorie === 'transferabil')
        .reduce((sum, r) => sum + (r.credite_acordate || 0), 0),
    };

    
    const totalCredits = (creditsByCategory.curricular || 0) + (creditsByCategory.extracurricular || 0) + (creditsByCategory.transferabil || 0);
    const pendingRequests = requestsData?.filter(r => r.status === 'in_asteptare').length || 0;

    setStats({
      totalCredits: totalCredits || 0,
      pendingRequests: pendingRequests || 0,
      validatedRequests: validatedRequests.length || 0,
      activeModules: 0,
      creditsByCategory,
    });

    setRecentRequests(requestsData?.slice(0, 10) || []);

    setQuickActions([
      { icon: <RocketIcon />, label: 'Cerere nouă', color: '#2196F3', path: '/new-request' },
      { icon: <PsychologyIcon />, label: 'Vezi cereri', color: '#4CAF50', path: '/my-requests' },
      { icon: <LightbulbIcon />, label: 'Sugestii', color: '#9C27B0', path: '/suggestions' },
      { icon: <PdfIcon />, label: 'Ghid Credite', color: '#E91E63', path: null, action: 'pdf' },
    ]);
  };

  const loadTeacherDashboard = async () => {
    const { data: validTypes } = await supabase
      .from(TABLES.credit_types)
      .select('id')
      .in('validator_rol', ['profesor', 'coordonator']);

    const validTypeIds = validTypes?.map(t => t.id) || [];
    
    let requestsData = [];
    if (validTypeIds.length > 0) {
      const { data } = await supabase
        .from(TABLES.credit_requests)
        .select('*')
        .eq('status', 'in_asteptare')
        .in('id_tip_credit', validTypeIds)
        .order('data_cerere', { ascending: false });
      requestsData = data || [];
    }

    const { data: studentsData } = await supabase
      .from(TABLES.profiles)
      .select('id')
      .eq('rol', 'elev');

    setStats({
      requestsToReview: requestsData.length,
      totalStudents: studentsData?.length || 0,
    });

    setRecentRequests(requestsData.slice(0, 5) || []);

    setQuickActions([
      { icon: <CheckCircleIcon />, label: 'Validează cereri', color: '#4CAF50', path: '/review-requests' },
      { icon: <PeopleIcon />, label: 'Vezi elevi', color: '#2196F3', path: '/teacher-students' },
    ]);
  };

  const loadCoordinatorDashboard = async () => {
    const { data: studentsData } = await supabase
      .from(TABLES.profiles)
      .select('*')
      .eq('rol', 'elev');

    const { data: requestsData } = await supabase
      .from(TABLES.credit_requests)
      .select('*')
      .eq('status', 'in_asteptare')
      .order('data_cerere', { ascending: false });

    const totalStudents = studentsData?.length || 0;
    const requestsToReview = requestsData?.length || 0;

    setStats({
      totalStudents,
      requestsToReview,
    });

    setRecentRequests(requestsData?.slice(0, 5) || []);

    setQuickActions([
      { icon: <CheckCircleIcon />, label: 'Validă cereri', color: '#4CAF50', path: '/review-requests' },
      { icon: <PeopleIcon />, label: 'Sistem Inteligent', color: '#2196F3', path: '/intelligence' },
      { icon: <BarChartIcon />, label: 'Gestiune Clase', color: '#9C27B0', path: '/class-management' },
    ]);
  };

  const getDashboardTitle = () => {
    switch (profile?.rol) {
      case 'elev': return 'Bine ai venit!';
      case 'profesor': return 'Panou Profesor';
      case 'coordonator': return 'Panou Coordonator';
      default: return 'Dashboard';
    }
  };

  const getDashboardSubtitle = () => {
    switch (profile?.rol) {
      case 'elev': return 'Urmărește-ți progresul și câștigă credite!';
      case 'profesor': return 'Gestionează cererile elevilor tăi';
      case 'coordonator': return 'Monitorizează întregul sistem';
      default: return '';
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bună dimineața';
    if (hour < 18) return 'Bună ziua';
    return 'Bună seara';
  };

  const getSpecializationName = (code) => {
    const map = {
      'MI': 'Matematică-Informatică',
      'SN': 'Științe ale Naturii',
      'SS': 'Științe Sociale',
      'FL': 'Filologie',
      'BU': 'Business',
    };
    return map[code] || code;
  };

  if (authLoading || loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, px: { xs: 0, sm: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh" flexDirection="column">
          <CircularProgress size={80} thickness={4} sx={{ mb: 3 }} />
          <Typography variant="h5" fontWeight="medium">
            Se încarcă panoul tău personal...
          </Typography>
        </Box>
      </Container>
    );
  }

  const studentStats = [
    { icon: <TrophyIcon />, value: stats.totalCredits, label: 'Credite Totale', color: '#4CAF50' },
    { icon: <CheckCircleIcon />, value: stats.validatedRequests, label: 'Cereri Aprobate', color: '#2196F3', valueColor: 'success.main' },
    { icon: <HourglassEmptyIcon />, value: stats.pendingRequests, label: 'În Așteptare', color: '#FF9800', valueColor: 'warning.main' },
    { icon: <TrendingUpIcon />, value: stats.activeModules, label: 'Module Active', color: '#9C27B0', valueColor: 'secondary' },
  ];

  const creditCategories = [
    { label: 'Curriculare', value: stats.creditsByCategory?.curricular || 0, bgcolor: '#E3F2FD', color: '#1976D2' },
    { label: 'Extracurriculare', value: stats.creditsByCategory?.extracurricular || 0, bgcolor: '#FFF3E0', color: '#F57C00' },
    { label: 'Transferabile', value: stats.creditsByCategory?.transferabil || 0, bgcolor: '#F3E5F5', color: '#7B1FA2' },
    {
      label: 'Total',
      value: (stats.creditsByCategory?.curricular || 0) + (stats.creditsByCategory?.extracurricular || 0) + (stats.creditsByCategory?.transferabil || 0),
      bgcolor: '#E8F5E9',
      color: '#2E7D32',
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3, px: { xs: 0, sm: 3 } }}>
      {/* Hero Header */}
      <Zoom in={true}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 4 },
            mb: 4,
            borderRadius: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              right: -50,
              top: -50,
              opacity: 0.1,
            }}
          >
            <TrophyIcon sx={{ fontSize: 300 }} />
          </Box>
          
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                  {getGreeting()}, {profile?.prenume}!
                </Typography>
                <Typography
                  variant="h2"
                  fontWeight="bold"
                  sx={{ fontSize: { xs: '1.75rem', md: '3rem' }, lineHeight: 1.2 }}
                >
                  {getDashboardTitle()}
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, mt: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {getDashboardSubtitle()}
                </Typography>
                {profile?.rol === 'elev' && selectedYear && (
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <SchoolYearSelect value={selectedYear} options={yearOptions} onChange={setSelectedYear} />
                    <SchoolYearBadge year={selectedYear} />
                  </Box>
                )}
              </Box>

              <Avatar
                sx={{
                  width: { xs: 64, md: 80 },
                  height: { xs: 64, md: 80 },
                  bgcolor: 'rgba(255,255,255,0.2)',
                  border: '3px solid rgba(255,255,255,0.3)',
                  fontSize: { xs: '1.5rem', md: '2rem' },
                }}
              >
                {profile?.prenume?.[0]}{profile?.nume?.[0]}
              </Avatar>
            </Box>
            
            {profile?.specializare && (
              <Chip
                label={getSpecializationName(profile.specializare)}
                sx={{
                  mt: 3,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontSize: '0.9rem',
                  fontWeight: 'medium',
                  backdropFilter: 'blur(10px)',
                }}
                icon={<SchoolIcon />}
              />
            )}
          </Box>
        </Paper>
      </Zoom>

      {profile?.rol === 'elev' && selectedYear && selectedYear !== 'all' && stats.validatedRequests === 0 && stats.pendingRequests === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Nu ai activitate în anul {selectedYear} încă.
        </Alert>
      )}

      {/* Quick Actions */}
      <Fade in={true} timeout={800}>
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
            Acțiuni Rapide
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%' }}>
            {quickActions.map((action, index) => (
              <Box key={index} sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(25% - 18px)' }, display: 'flex' }}>
                <Card
                  onClick={() => {
                    if (action.action === 'pdf') {
                      // deschide ghidul de credite
                      setGuideDialogOpen(true);
                    } else if (action.path) {
                      navigate(action.path);
                    }
                  }}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    borderRadius: 3,
                    border: '2px solid transparent',
                    background: `linear-gradient(135deg, white 0%, ${alpha(action.color, 0.05)} 100%)`,
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      borderColor: alpha(action.color, 0.3),
                      boxShadow: `0 10px 20px ${alpha(action.color, 0.2)}`,
                    },
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center',
                    p: { xs: 2, sm: 3 },
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: alpha(action.color, 0.1),
                      color: action.color,
                      width: { xs: 52, sm: 60 },
                      height: { xs: 52, sm: 60 },
                      mb: 2,
                    }}
                  >
                    {action.icon}
                  </Avatar>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
                    {action.label}
                  </Typography>
                  <Button
                    variant="text"
                    endIcon={<ChevronRightIcon />}
                    sx={{ color: action.color, fontWeight: 'bold', width: { xs: '100%', sm: 'auto' }, textTransform: 'none' }}
                  >
                    {action.action === 'pdf' ? 'Deschide' : 'Accesează'}
                  </Button>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>
      </Fade>


      {/* Stats Cards */}
      <Fade in={true} timeout={1000}>
        <Box sx={{ mb: 5 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
            Statistici
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, width: '100%' }}>
            {profile.rol === 'elev' && (
              <>
                <Box sx={{ width: '100%' }}>
                  <StudentStats stats={studentStats} />
                </Box>

                <Box sx={{ width: '100%' }}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        Credite pe Categorii
                      </Typography>
                      <CategoryCredits categories={creditCategories} />
                    </CardContent>
                  </Card>
                </Box>
              </>
            )}

            {profile.rol === 'profesor' && (
              <>
                <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(50% - 12px)' }, display: 'flex' }}>
                  <Card sx={{ borderRadius: 3, height: '100%', width: '100%', minHeight: 120 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: alpha('#FF9800', 0.1), color: '#FF9800', mr: 2 }}>
                          <HourglassEmptyIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h3" fontWeight="bold" color="warning.main" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                            {stats.requestsToReview}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Cereri de Revizuit
                          </Typography>
                        </Box>
                      </Box>
                      <Button 
                        variant="contained" 
                        startIcon={<CheckCircleIcon />}
                        onClick={() => navigate('/review-requests')}
                        sx={{ mt: 2 }}
                      >
                        Vezi Cererile
                      </Button>
                    </CardContent>
                  </Card>
                </Box>

                <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(50% - 12px)' }, display: 'flex' }}>
                  <Card sx={{ borderRadius: 3, height: '100%', width: '100%', minHeight: 120 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: alpha('#2196F3', 0.1), color: '#2196F3', mr: 2 }}>
                          <PeopleIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                            {stats.totalStudents}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Elevi în Sistem
                          </Typography>
                        </Box>
                      </Box>
                      <Button 
                        variant="outlined" 
                        startIcon={<PeopleIcon />}
                        onClick={() => navigate('/teacher-students')}
                        sx={{ mt: 2 }}
                      >
                        Vezi Elevii
                      </Button>
                    </CardContent>
                  </Card>
                </Box>
              </>
            )}

            {profile.rol === 'coordonator' && (
              <>
                <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(50% - 12px)' }, display: 'flex' }}>
                  <Card sx={{ borderRadius: 3, height: '100%', width: '100%', minHeight: 120 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: alpha('#2196F3', 0.1), color: '#2196F3', mr: 2 }}>
                          <PeopleIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                            {stats.totalStudents}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Elevi
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>

                <Box sx={{ flex: { xs: '0 0 calc(50% - 8px)', sm: '0 0 calc(50% - 12px)' }, display: 'flex' }}>
                  <Card sx={{ borderRadius: 3, height: '100%', width: '100%', minHeight: 120 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: alpha('#FF9800', 0.1), color: '#FF9800', mr: 2 }}>
                          <HourglassEmptyIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h3" fontWeight="bold" color="warning.main" sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem' } }}>
                            {stats.requestsToReview}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Cereri În Așteptare
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Fade>

      {/* Recent Activity */}
      {recentRequests.length > 0 ? (
        <Fade in={true} timeout={1200}>
          <Grid container spacing={3}>
            {recentRequests.length > 0 && (
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3, height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <BoltIcon sx={{ mr: 1, color: 'warning.main' }} />
                      Activitate Recentă
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    {recentRequests.slice(0, 5).map((request, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 2,
                          mb: 2,
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.background.default, 0.5),
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                          },
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor:
                              request.status === 'validat'
                                ? alpha('#4CAF50', 0.1)
                                : request.status === 'respins'
                                ? alpha('#F44336', 0.1)
                                : alpha('#FF9800', 0.1),
                            color:
                              request.status === 'validat'
                                ? '#4CAF50'
                                : request.status === 'respins'
                                ? '#F44336'
                                : '#FF9800',
                            mr: 2,
                          }}
                        >
                          {request.status === 'validat' ? <CheckCircleIcon /> : <HourglassEmptyIcon />}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {request.nume_activitate}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(request.data_cerere).toLocaleDateString('ro-RO')}
                          </Typography>
                        </Box>
                        <Chip
                          label={request.status}
                          size="small"
                          color={
                            request.status === 'validat'
                              ? 'success'
                              : request.status === 'respins'
                              ? 'error'
                              : 'warning'
                          }
                          variant="outlined"
                        />
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Fade>
      ) : null}

      {/* Dialog Ghid Credite */}
      <Dialog
        open={guideDialogOpen}
        onClose={() => setGuideDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2
        }}>
          <PdfIcon color="error" />
          <Typography variant="h6" fontWeight="bold">
            Ghid Credite - Sistemul de Creditare
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" color="primary" gutterBottom fontWeight="bold">
              Credite Curriculare
            </Typography>
            <Typography variant="body2" paragraph>
              Sunt creditele obținute prin activități din cadrul programului școlar oficial.
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              • Participare la cursuri obligatorii<br/>
              • Proiecte școlare și teme<br/>
              • Evaluări și examene<br/>
              • Activități de laborator
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" color="secondary" gutterBottom fontWeight="bold">
              Credite Extracurriculare
            </Typography>
            <Typography variant="body2" paragraph>
              Sunt creditele obținute prin activități desfășurate în afara programului școlar.
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              • Participare la olimpiade și concursuri<br/>
              • Voluntariat și activități comunitare<br/>
              • Cursuri și certificări suplimentare<br/>
              • Proiecte personale relevante<br/>
              • Participare la conferințe și workshop-uri
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#FF9800' }} gutterBottom fontWeight="bold">
              Credite Transferabile
            </Typography>
            <Typography variant="body2" paragraph>
              Sunt creditele care pot fi transferate de la un an școlar la altul sau între instituții.
            </Typography>
            <Typography variant="body2" component="div" sx={{ pl: 2 }}>
              • Credite acumulate în anii anteriori<br/>
              • Credite recunoscute de la alte instituții<br/>
              • Credite obținute prin programe de schimb<br/>
              • Credite din certificări recunoscute internațional
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ 
            bgcolor: alpha(theme.palette.info.main, 0.1), 
            p: 2, 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'info.main'
          }}>
            <Typography variant="subtitle2" color="info.main" gutterBottom fontWeight="bold">
              Informații importante
            </Typography>
            <Typography variant="body2">
              • Creditele necesare sunt stabilite de fiecare profesor pentru modulul său<br/>
              • Poți vedea progresul pentru fiecare modul în pagina "Cereri"<br/>
              • Creditele transferabile pot fi folosite pentru a completa modulele active
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button 
            variant="outlined" 
            onClick={() => window.print()}
            startIcon={<PdfIcon />}
          >
            Printează
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setGuideDialogOpen(false)}
          >
            Închide
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

